import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { VitalSignsInput } from "./VitalSignsInput";

interface PhysicalExamData {
  vital_signs?: {
    blood_pressure_systolic?: number;
    blood_pressure_diastolic?: number;
    heart_rate?: number;
    respiratory_rate?: number;
    temperature?: number;
    oxygen_saturation?: number;
    weight?: number;
    height?: number;
  };
  general_appearance?: string;
  head_neck?: string;
  cardiovascular?: string;
  respiratory?: string;
  abdomen?: string;
  musculoskeletal?: string;
  neurological?: string;
  skin?: string;
  other_findings?: string;
}

interface PhysicalExamSectionProps {
  data: PhysicalExamData;
  onChange: (data: PhysicalExamData) => void;
}

export function PhysicalExamSection({ data, onChange }: PhysicalExamSectionProps) {
  const updateField = (field: keyof PhysicalExamData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-4">
      <VitalSignsInput
        values={data.vital_signs || {}}
        onChange={(vitalSigns) => updateField("vital_signs", vitalSigns)}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Exame Físico por Sistemas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="general_appearance">Aspecto Geral</Label>
            <Textarea
              id="general_appearance"
              placeholder="Estado geral, nível de consciência, hidratação..."
              rows={2}
              value={data.general_appearance || ""}
              onChange={(e) => updateField("general_appearance", e.target.value)}
            />
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="head_neck">Cabeça e Pescoço</Label>
              <Textarea
                id="head_neck"
                placeholder="Inspeção, palpação, ausculta..."
                rows={3}
                value={data.head_neck || ""}
                onChange={(e) => updateField("head_neck", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cardiovascular">Cardiovascular</Label>
              <Textarea
                id="cardiovascular"
                placeholder="Ausculta cardíaca, pulsos, edemas..."
                rows={3}
                value={data.cardiovascular || ""}
                onChange={(e) => updateField("cardiovascular", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="respiratory">Respiratório</Label>
              <Textarea
                id="respiratory"
                placeholder="Inspeção, palpação, percussão, ausculta..."
                rows={3}
                value={data.respiratory || ""}
                onChange={(e) => updateField("respiratory", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="abdomen">Abdome</Label>
              <Textarea
                id="abdomen"
                placeholder="Inspeção, ausculta, palpação, percussão..."
                rows={3}
                value={data.abdomen || ""}
                onChange={(e) => updateField("abdomen", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="musculoskeletal">Musculoesquelético</Label>
              <Textarea
                id="musculoskeletal"
                placeholder="Inspeção, palpação, amplitude de movimento..."
                rows={3}
                value={data.musculoskeletal || ""}
                onChange={(e) => updateField("musculoskeletal", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="neurological">Neurológico</Label>
              <Textarea
                id="neurological"
                placeholder="Consciência, força, sensibilidade, reflexos..."
                rows={3}
                value={data.neurological || ""}
                onChange={(e) => updateField("neurological", e.target.value)}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="skin">Pele e Anexos</Label>
            <Textarea
              id="skin"
              placeholder="Cor, temperatura, lesões, cicatrizes..."
              rows={2}
              value={data.skin || ""}
              onChange={(e) => updateField("skin", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="other_findings">Outros Achados</Label>
            <Textarea
              id="other_findings"
              placeholder="Achados adicionais relevantes..."
              rows={2}
              value={data.other_findings || ""}
              onChange={(e) => updateField("other_findings", e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
